import React from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useSuperAdmin } from '../../contexts/SuperAdminContext';
import {
  HiOutlineHome,
  HiOutlineOfficeBuilding,
  HiOutlineClipboardList,
  HiOutlineUsers,
  HiOutlineChartBar,
  HiOutlineMenu,
  HiOutlineX,
  HiOutlineLogout,
  HiOutlineCog,
  HiOutlinePhone,
} from 'react-icons/hi';

const navItems = [
  { path: '/superadmin/dashboard', label: 'Dashboard', icon: HiOutlineHome },
  { path: '/superadmin/organizations', label: 'Organizations', icon: HiOutlineOfficeBuilding },
  { path: '/superadmin/plans', label: 'Plans', icon: HiOutlineClipboardList },
  // { path: '/superadmin/leads', label: 'Leads', icon: HiOutlinePhone },
  { path: '/superadmin/admins', label: 'Admin Users', icon: HiOutlineUsers },
  // { path: '/superadmin/analytics', label: 'Analytics', icon: HiOutlineChartBar },
  // { path: '/superadmin/settings', label: 'Settings', icon: HiOutlineCog },
];

const SuperAdminSidebar = ({ isOpen, onClose }) => {
  const { superAdmin, logout } = useSuperAdmin();
  const location = useLocation();
  const navigate = useNavigate();

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const handleNavClick = () => {
    if (window.innerWidth < 1024) onClose();
  };

  const handleLogout = () => {
    logout();
    navigate('/superadmin/login');
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 z-50 h-full w-64
          bg-gradient-to-b from-gray-900 to-gray-800
          flex flex-col
          transform transition-transform duration-300 ease-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0
        `}
      >
        {/* Logo */}
        <div className="flex items-center gap-3 px-4 py-4 border-b border-gray-700">
          <div className="w-10 h-10 rounded-xl bg-primary-500 flex items-center justify-center shadow-lg">
            <span className="text-white text-xl">🔐</span>
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="font-bold text-white text-lg">TeleValley</h1>
            <p className="text-xs text-gray-400">SuperAdmin Panel</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-700 lg:hidden transition-colors"
          >
            <HiOutlineX className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* User Info */}
        <div className="px-4 py-3">
          <div className="px-3 py-3 rounded-xl bg-gray-700/50 border border-gray-600">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center flex-shrink-0">
                <span className="text-white font-semibold text-sm">
                  {superAdmin?.name?.charAt(0) || 'S'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-white text-sm truncate">
                  {superAdmin?.name || 'Super Admin'}
                </p>
                <p className="text-xs text-gray-400 truncate">
                  {superAdmin?.role || 'super_admin'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-3 py-2">
          <p className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
            Menu
          </p>
          <ul className="space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);

              return (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    onClick={handleNavClick}
                    className={`
                      flex items-center gap-3 px-3 py-2.5 rounded-xl
                      transition-all duration-200 relative
                      ${active
                        ? 'bg-primary-500 text-white shadow-lg shadow-primary-500/25'
                        : 'text-gray-300 hover:bg-gray-700/50 hover:text-white'
                      }
                    `}
                  >
                    <Icon className="w-5 h-5 flex-shrink-0" />
                    <span className="text-sm font-medium">{item.label}</span>
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Logout */}
        <div className="p-3 border-t border-gray-700">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-gray-300 hover:bg-red-500/10 hover:text-red-400 transition-all duration-200"
          >
            <HiOutlineLogout className="w-5 h-5 flex-shrink-0" />
            <span className="text-sm font-medium">Logout</span>
          </button>
        </div>
      </aside>
    </>
  );
};

export const SuperAdminMobileHeader = ({ onMenuClick }) => {
  const { superAdmin } = useSuperAdmin();

  return (
    <header className="lg:hidden fixed top-0 left-0 right-0 z-30 bg-gray-900/95 backdrop-blur-lg border-b border-gray-700">
      <div className="flex items-center justify-between px-4 py-2.5">
        <button
          onClick={onMenuClick}
          className="p-2 -ml-2 rounded-lg text-gray-300 hover:bg-gray-700 transition-colors"
        >
          <HiOutlineMenu className="w-6 h-6" />
        </button>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center shadow-md">
            <span className="text-white">🔐</span>
          </div>
          <span className="font-bold text-white">TeleValley</span>
        </div>
        <div className="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center">
          <span className="text-white font-semibold text-xs">
            {superAdmin?.name?.charAt(0) || 'S'}
          </span>
        </div>
      </div>
    </header>
  );
};

export default SuperAdminSidebar;